import type { FC } from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import type { AxiosError } from 'axios';
import { useAtom } from 'jotai';

import {
  ReportOrderStatus,
  type ReportOrderStatusOrSentinel,
  ReportStatus,
} from 'src/constants/report';
import { SECTION } from 'src/constants/section';
import { API_RESULT_LIMITS } from 'src/constants/settings';
import type { ModuleOrPlanActionOrgUnit } from 'src/types/orgUnit';
import type { Report } from 'src/types/report';
import useSurveySettings from 'src/hooks/useSurveySettings';
import { prioritizeZip, sortByLanguageName } from 'src/helper/helper';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import ErrorMessage from 'src/components/common/Messages/ErrorMessage';
import { reportCache } from 'src/components/report/atoms';
import { orderReport } from 'src/components/report/helper/helper';
import { ReportStatusContext } from 'src/components/report/OrgUnitReportsPage/ReportStatusContext';
import ReportHeader from 'src/components/report/ReportHeader';
import ReportTable from 'src/components/report/ReportTable';
import ShoppingCartContext from 'src/components/report/ShoppingCartContext';

import { styles } from './index.styles';

interface ReportOverviewProps {
  orgUnit?: ModuleOrPlanActionOrgUnit;
}

const ReportOverview: FC<ReportOverviewProps> = ({ orgUnit }) => {
  const { t } = useTranslation('report');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [reports, setReports] = useAtom(reportCache);
  const [reportsAvailable, setReportsAvailable] = useState(true);
  const { minRespondentAmount } = useSurveySettings();
  const { reportsStatus, setPendingReportIds } =
    useContext(ReportStatusContext);
  const {
    shoppingCartLanguage,
    replaceEquivalentReports,
    shoppingCartItems,
    canUseShoppingCart,
  } = useContext(ShoppingCartContext);
  const shoppingCartLanguageRef = useRef(shoppingCartLanguage);

  /**
   * @description Fetch all reports for the OrgUnit.
   */
  useEffect(() => {
    if (orgUnit && !isLoading) fetchReportsByOrgUnit(orgUnit.id);
  }, [orgUnit]);

  /**
   * @description After a language switch the backoff of the old language should be stopped since only the reports
   * of the currently selected language are of interest. Furthermore, if there are equivalent reports they should
   * be added to the backoff and ordered since this is a desired feature.
   */
  useEffect(() => {
    if (shoppingCartLanguageRef.current === shoppingCartLanguage) return;

    const cachedReports = Object.values(reports).flat();
    const equivalentReports = replaceEquivalentReports(cachedReports);
    const draftReports: number[] = [];

    shoppingCartLanguageRef.current = shoppingCartLanguage;
    equivalentReports.forEach((report) => {
      if (report.order_status === ReportOrderStatus.DRAFT) {
        draftReports.push(report.id);
        orderReport(report.id);
      }
    });

    // since reports of all languages will be loaded when selecting an OrgUnit,
    // only the reports that have to be ordered after a language switch need to be added
    setPendingReportIds((prevState) => [...prevState, ...draftReports]);
  }, [shoppingCartLanguage]);

  /**
   * @description this method resets the state to its defaults and starts fetching
   * reports via our HttpService.
   */
  const fetchReportsByOrgUnit = (orgUnitId: number) => {
    const requestEndpoint = apiPaths.REPORTS.LIST;
    const requestParams = {
      limit: API_RESULT_LIMITS.SEARCH,
      organisational_unit: orgUnitId,
      module_id: SECTION.GET_REPORT,
    };

    setErrorMessage(null);
    setIsLoading(true);

    HttpService.get(requestEndpoint, requestParams)
      .then(handleResponse)
      .catch(handleError);
  };

  /**
   * @description this method handles errors from our HttpService and updates the state with the specific data.
   * @param {AxiosError} error - an error object
   */
  const handleError = (error: AxiosError) => {
    if (error.response?.statusText) setErrorMessage(error.response.statusText);
    setIsLoading(false);
  };

  /**
   * @description this method handles responses from our HttpService and sorts the reports from the response and
   * updates the component state. It also checks, whether the reports are available or if a message has to be displayed.
   * @param {Object} response - an HttpService response object
   */
  const handleResponse = (response: { data: { results: Report[] } }) => {
    const {
      data: { results: data },
    } = response;
    const newReports = data.sort(sortByLanguageName);
    const cartItemIds = shoppingCartItems.map(({ id }) => id);
    // add all pending report ids to the backoff and take shopping cart items into account
    // because reports of different org units may be added to the shopping cart
    setPendingReportIds((prevState) => {
      const pendingStatuses: ReportOrderStatusOrSentinel[] = [
        ReportOrderStatus.ORDERED,
        ReportOrderStatus.IN_PRODUCTION,
      ];
      return data.reduce(
        (acc: number[], report) =>
          !prevState.includes(report.id) &&
          pendingStatuses.includes(report.order_status)
            ? [...acc, report.id]
            : acc,
        prevState.filter((pendingReportId) =>
          cartItemIds.includes(pendingReportId),
        ),
      );
    });

    // Checks report status for availability.
    const reportsAvailable =
      newReports.findIndex(
        (report) => report.status !== ReportStatus.AVAILABLE,
      ) === -1;
    orgUnit && setReports({ ...reports, [orgUnit.id]: newReports || [] });
    setIsLoading(false);
    setReportsAvailable(reportsAvailable);
  };

  const getCurrentReports = (orgUnitId: number) => {
    // Spread reports into a new array because the reports dict is read-only
    const currentReports = [...(reports[orgUnitId] || [])];
    if (canUseShoppingCart) {
      return currentReports
        .filter(({ language }) => language === shoppingCartLanguage?.code)
        .sort(prioritizeZip);
    }
    return currentReports;
  };

  return errorMessage ? (
    <ErrorMessage>
      {t('common:error')}: {errorMessage}
    </ErrorMessage>
  ) : orgUnit ? (
    <Box sx={styles.wrapper}>
      <Box sx={styles.headerWrapper}>
        <ReportHeader
          reportOwnerId={orgUnit.org_unit_id}
          reportOwnerName={orgUnit.name}
          showFollowUpInspirations={orgUnit.follow_up_inspirations_link}
        />
      </Box>
      {!reportsAvailable && (
        <Typography sx={styles.reportAnnotation}>
          {t('reportAnnotation', { minRespondentAmount })}
        </Typography>
      )}
      <ReportTable
        reports={getCurrentReports(orgUnit.id)}
        loading={isLoading}
        reportsStatus={reportsStatus}
      />
    </Box>
  ) : (
    <Box sx={styles.panel}>{t('selectLeafInfo')}</Box>
  );
};

export default ReportOverview;
